import type {
    PvEtudeEcoFullForm,
    PvEtudeEcoPlageHoraire,
    PvEtudeEcoSubscribeForm,
} from "../types/pv-etude-eco-form.types"

function defaultSubscribe(): PvEtudeEcoSubscribeForm {
    return {
        pricing_type: "base",
        base: {
            prix_abonnement: 12.6,
            prix_kwh: 25.16,
        },
        hphc: {
            prix_abonnement: 13.01,
            prix_kwh_hp: 27.0,
            prix_kwh_hc: 20.68,
            plage: [] as PvEtudeEcoPlageHoraire[],
        },
        tempo: {
            prix_abonnement: 1.08,
            prix_buhp: 16.09,
            prix_buhc: 12.96,
            prix_bchp: 18.94,
            prix_bchc: 14.86,
            prix_rhp: 75.62,
            prix_rhc: 15.68,
        },
        hs: {
            prix_abonnement: 3.33,
            prix_kwh_hph: 9.601,
            prix_kwh_hch: 6.913,
            prix_kwh_hpb: 5.031,
            prix_kwh_hcb: 3.476,
            plage: [] as PvEtudeEcoPlageHoraire[],
        },
    }
}

/**
 * Valeurs par défaut du formulaire (chiffres et `plage: []` comme `DEFAULT_FORM` dans
 * `homeys-legacy/.../FormPVEtudeEco.vue`).
 */
export function createDefaultPvEtudeEcoForm(): PvEtudeEcoFullForm {
    return {
        name_analyse: "",
        general_info: {
            prix_installation: null,
            duree_vie_installation: 20,
            prix_ondulateur: null,
            duree_vie_ondulateur: 10,
            taux_inflation: 2,
            taux_actualisation: 0,
            prix_maintenance: null,
        },
        subscribe_before: defaultSubscribe(),
        subscribe_after: defaultSubscribe(),
        price: {
            ndj: 13.0,
            fma: 12.97,
            mjj: 13.01,
            aso: 13.39,
        },
    }
}

/** Contrainte nom d’analyse (caractères autorisés côté tâche AWS), aligné legacy. */
export function isValidAwsString(value: string): boolean {
    if (!value.trim()) return false
    return /^[a-zA-Z0-9\-_. ]*$/.test(value)
}

export function isValidDecimalPlaces(
    value: number,
    maxDecimals: number
): boolean {
    const s = String(value)
    const parts = s.split(".")
    const dec = parts[1]?.length ?? 0
    return dec <= maxDecimals
}

/** Exposé pour la validation vee-validate / Yup (mêmes règles que `getIsPvEtudeEcoFormValid`). */
export function isValidNumericField(key: string, value: unknown): boolean {
    if (value === null || value === undefined) return false
    if (typeof value !== "number" || Number.isNaN(value)) return false
    if (key.includes("prix") || key.includes("prix_")) {
        return isValidDecimalPlaces(value, 4)
    }
    return true
}

function isPlageListValid(plages: PvEtudeEcoPlageHoraire[]): boolean {
    if (!Array.isArray(plages) || plages.length === 0) return false
    return plages.every((p) => Boolean(p.time_from) && Boolean(p.time_to))
}

function isSubscribeBlockValid(sub: PvEtudeEcoSubscribeForm): boolean {
    const slice = sub[sub.pricing_type] as Record<string, unknown>
    return Object.entries(slice).every(([key, value]) => {
        if (key === "plage") {
            return isPlageListValid(value as PvEtudeEcoPlageHoraire[])
        }
        return isValidNumericField(key, value)
    })
}

/**
 * Indique si toutes les sections requises du formulaire sont renseignées (règles legacy).
 */
export function getIsPvEtudeEcoFormValid(form: PvEtudeEcoFullForm): boolean {
    const gi = form.general_info
    const giOk = Object.entries(gi).every(([key, value]) =>
        isValidNumericField(key, value)
    )
    const priceOk = Object.entries(form.price).every(([key, value]) =>
        isValidNumericField(key, value)
    )
    return (
        giOk &&
        priceOk &&
        isSubscribeBlockValid(form.subscribe_before) &&
        isSubscribeBlockValid(form.subscribe_after)
    )
}

/**
 * Corps `data` attendu par `PvService.createEtudeEco` (mapping legacy `onClickSubmit`).
 */
export function buildPvEtudeEcoCreatePayload(
    form: PvEtudeEcoFullForm
): Record<string, unknown> {
    const gi = form.general_info
    return {
        prix_installation: gi.prix_installation,
        duree_vie_installation: gi.duree_vie_installation,
        prix_onduleur: gi.prix_ondulateur,
        duree_vie_onduleur: gi.duree_vie_ondulateur,
        taux_inflation: gi.taux_inflation,
        taux_actualisation: gi.taux_actualisation,
        prix_maintenance: gi.prix_maintenance,
        soutirage_avant: {
            pricing_type: form.subscribe_before.pricing_type,
            ...form.subscribe_before[form.subscribe_before.pricing_type],
        },
        soutirage_apres: {
            pricing_type: form.subscribe_after.pricing_type,
            ...form.subscribe_after[form.subscribe_after.pricing_type],
        },
        injection_price: {
            pricing_type: "4S",
            prix_ndj: form.price.ndj,
            prix_fma: form.price.fma,
            prix_mjj: form.price.mjj,
            prix_aso: form.price.aso,
        },
    }
}
